import { useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import * as client from "../../../client";
import { formatExactMoneyDollars } from "../Money";
import { StreetViewModal, StreetViewModalState } from "../sov/StreetViewModal";
import { PERIL_DISPLAY_NAMES } from "./types";
import styles from "./CatCard.module.css";

interface AalDriversTabProps {
  topLocationsByPeril: { [key: string]: client.CatTopLocation[] };
  reportId: string;
}

export const AalDriversTab: React.FC<AalDriversTabProps> = ({
  topLocationsByPeril,
  reportId,
}) => {
  const [topAddressPeril, setTopAddressPeril] = useState<string | null>(null);
  const [streetViewState, setStreetViewState] =
    useState<StreetViewModalState>(undefined);

  const topAddressPerilOptions = useMemo(() => {
    return Object.keys(topLocationsByPeril)
      .sort((a, b) => {
        const labelA = PERIL_DISPLAY_NAMES.get(a) ?? a;
        const labelB = PERIL_DISPLAY_NAMES.get(b) ?? b;
        return labelA.localeCompare(labelB);
      })
      .map((peril) => ({
        label: PERIL_DISPLAY_NAMES.get(peril) ?? peril,
        value: peril,
      }));
  }, [topLocationsByPeril]);

  const topAddressRows = useMemo(() => {
    const peril = topAddressPeril ?? topAddressPerilOptions[0]?.value;
    if (!peril) return [];
    return topLocationsByPeril[peril] ?? [];
  }, [topLocationsByPeril, topAddressPeril, topAddressPerilOptions]);

  return (
    <>
      <div className={styles.topAddressesToolbar}>
        <Dropdown
          value={topAddressPeril ?? topAddressPerilOptions[0]?.value}
          options={topAddressPerilOptions}
          onChange={(e) => setTopAddressPeril(e.value)}
          placeholder="Select peril"
          className={styles.layerDropdown}
        />
      </div>
      <DataTable
        value={topAddressRows}
        size="small"
        className={styles.compactTable}
        dataKey="address.address_id"
      >
        <Column
          header="#"
          body={(_rowData, { rowIndex }) => rowIndex + 1}
          style={{ width: "40px" }}
          align="center"
        />
        <Column
          header="Address"
          body={(row: client.CatTopLocation) => {
            const addr = row.address;
            if (!addr) return "";
            const locality = [addr.city, addr.state].filter(Boolean).join(", ");
            const secondLine = [locality, addr.zip].filter(Boolean).join(" ");
            return (
              <div>
                <div>{addr.street_address}</div>
                {secondLine && (
                  <div className={styles.addressSecondary}>{secondLine}</div>
                )}
              </div>
            );
          }}
        />
        <Column
          header="TIV"
          body={(row: client.CatTopLocation) =>
            row.address?.tiv != null
              ? formatExactMoneyDollars(row.address.tiv, {
                  withRounding: true,
                  withDollarSign: true,
                })
              : ""
          }
          style={{ width: "120px" }}
          align="right"
        />
        <Column
          header="% TIV"
          body={(row: client.CatTopLocation) =>
            row.tiv_pct != null ? `${(row.tiv_pct * 100).toFixed(1)}%` : ""
          }
          style={{ width: "80px" }}
          align="right"
        />
        <Column
          header="GU AAL"
          body={(row: client.CatTopLocation) =>
            row.aal != null
              ? formatExactMoneyDollars(row.aal, {
                  withRounding: true,
                  withDollarSign: true,
                })
              : ""
          }
          style={{ width: "120px" }}
          align="right"
        />
        <Column
          header="% AAL"
          body={(row: client.CatTopLocation) =>
            row.aal_pct != null ? `${(row.aal_pct * 100).toFixed(1)}%` : ""
          }
          style={{ width: "80px" }}
          align="right"
        />
        <Column
          header=""
          body={(row: client.CatTopLocation) => {
            const addr = row.address;
            if (!addr || !addr.lat || !addr.long) return null;
            const lat = addr.lat;
            const long = addr.long;
            return (
              <Button
                icon="pi pi-map"
                text
                rounded
                severity="secondary"
                tooltip="Street View"
                tooltipOptions={{ position: "left" }}
                onClick={() =>
                  setStreetViewState({
                    street_address: addr.street_address,
                    lat: Number(lat),
                    long: Number(long),
                  })
                }
              />
            );
          }}
          style={{ width: "60px" }}
          align="center"
        />
      </DataTable>
      {streetViewState && (
        <StreetViewModal
          initialState={streetViewState}
          onHide={() => setStreetViewState(undefined)}
          reportId={reportId}
        />
      )}
    </>
  );
};
